import { useEffect, useState, useRef } from 'react';
import { Save, Plus, X, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getSettings, updateSettings, uploadFile } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newTrustPoint, setNewTrustPoint] = useState('');
  const pickerRef = useRef(null);

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    try {
      const res = await getSettings();
      setSettings(res.data);
    } catch (err) {
      toast.error('Не удалось загрузить настройки');
    } finally {
      setBusy(false);
    }
  };

  const persistDraft = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Настройки сохранены');
    } catch (err) {
      toast.error('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const addTrustPoint = () => {
    if (!newTrustPoint.trim()) return;
    setSettings({
      ...settings,
      trust_points: [...(settings.trust_points || []), newTrustPoint.trim()]
    });
    setNewTrustPoint('');
  };

  const removeTrustPoint = (index) => {
    setSettings({
      ...settings,
      trust_points: settings.trust_points.filter((_, i) => i !== index)
    });
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Файл слишком большой. Максимум 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const res = await uploadFile(file);
      const fullUrl = `${API_URL}${res.data.url}`;
      setSettings({ ...settings, background_image: fullUrl, background_type: 'image' });
      toast.success('Фон загружен');
    } catch (err) {
      toast.error('Не удалось загрузить');
    } finally {
      setIsUploading(false);
    }
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Настройки сайта</h1>
          <p className="text-slate-500">Основные настройки лендинга</p>
        </div>
        <Button onClick={persistDraft} disabled={saving} data-testid="save-settings-button">
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Сохранение...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Сохранить
            </span>
          )}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Main Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Заголовок и подзаголовок</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Заголовок (над карточкой менеджера)</Label>
              <Input
                value={settings.title}
                onChange={(e) => setSettings({ ...settings, title: e.target.value })}
                placeholder="Tempeo"
                data-testid="settings-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Подзаголовок</Label>
              <Input
                value={settings.subtitle}
                onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })}
                placeholder="Работа за рубежом с гарантией"
                data-testid="settings-subtitle-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Время ответа</Label>
              <Input
                value={settings.response_time}
                onChange={(e) => setSettings({ ...settings, response_time: e.target.value })}
                placeholder="5 минут"
                data-testid="settings-response-time-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Футер</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Год</Label>
              <Input
                value={settings.footer_year || '2024'}
                onChange={(e) => setSettings({ ...settings, footer_year: e.target.value })}
                placeholder="2024"
                data-testid="settings-footer-year-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Название компании (если пусто - берётся заголовок)</Label>
              <Input
                value={settings.footer_company || ''}
                onChange={(e) => setSettings({ ...settings, footer_company: e.target.value })}
                placeholder="Название компании"
                data-testid="settings-footer-company-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Background Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Фон сайта</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              type="file"
              ref={pickerRef}
              onChange={handleBackgroundUpload}
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
            />

            <div className="space-y-2">
              <Label>Тип фона</Label>
              <Select
                value={settings.background_type || 'color'}
                onValueChange={(value) => setSettings({ ...settings, background_type: value })}
              >
                <SelectTrigger data-testid="background-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="color">Сплошной цвет</SelectItem>
                  <SelectItem value="gradient">Градиент</SelectItem>
                  <SelectItem value="image">Картинка</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {settings.background_type === 'color' && (
              <div className="space-y-2">
                <Label>Цвет фона</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.background_color || '#F8FAFC'}
                    onChange={(e) => setSettings({ ...settings, background_color: e.target.value })}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={settings.background_color || '#F8FAFC'}
                    onChange={(e) => setSettings({ ...settings, background_color: e.target.value })}
                    placeholder="#F8FAFC"
                    data-testid="settings-bg-color-input"
                  />
                </div>
              </div>
            )}

            {settings.background_type === 'gradient' && (
              <div className="space-y-2">
                <Label>CSS градиент</Label>
                <Input
                  value={settings.background_gradient || ''}
                  onChange={(e) => setSettings({ ...settings, background_gradient: e.target.value })}
                  placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  data-testid="settings-bg-gradient-input"
                />
                {settings.background_gradient && (
                  <div
                    className="h-20 rounded-lg"
                    style={{ background: settings.background_gradient }}
                  />
                )}
              </div>
            )}

            {settings.background_type === 'image' && (
              <div className="space-y-2">
                <Label>Картинка фона</Label>
                {settings.background_image ? (
                  <div className="space-y-2">
                    <div
                      className="h-32 rounded-lg bg-cover bg-center"
                      style={{ backgroundImage: `url(${settings.background_image})` }}
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => pickerRef.current?.click()}
                      disabled={isUploading}
                    >
                      {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Заменить картинку
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => !isUploading && pickerRef.current?.click()}
                    className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-slate-300"
                  >
                    {isUploading ? (
                      <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Загрузить картинку</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Default Links */}
        <Card>
          <CardHeader>
            <CardTitle>Ссылки по умолчанию</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Telegram</Label>
              <Input
                value={settings.default_telegram}
                onChange={(e) => setSettings({ ...settings, default_telegram: e.target.value })}
                placeholder="https://t.me/username"
                data-testid="settings-telegram-input"
              />
              <p className="text-xs text-slate-500">Используется если нет активного менеджера</p>
            </div>

            <div className="space-y-2">
              <Label>WhatsApp</Label>
              <Input
                value={settings.default_whatsapp}
                onChange={(e) => setSettings({ ...settings, default_whatsapp: e.target.value })}
                placeholder="https://wa.me/1234567890"
                data-testid="settings-whatsapp-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Авто-сообщение</Label>
              <Textarea
                value={settings.auto_message || ''}
                onChange={(e) => setSettings({ ...settings, auto_message: e.target.value })}
                placeholder="Добрый день, меня интересует работа"
                rows={2}
                data-testid="settings-auto-message-input"
              />
              <p className="text-xs text-slate-500">Текст автоматически вставится в поле сообщения при открытии Telegram/WhatsApp</p>
            </div>
          </CardContent>
        </Card>

        {/* Trust Points */}
        <Card>
          <CardHeader>
            <CardTitle>Пункты доверия</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newTrustPoint}
                onChange={(e) => setNewTrustPoint(e.target.value)}
                placeholder="Официальные документы"
                onKeyDown={(e) => e.key === 'Enter' && addTrustPoint()}
                data-testid="settings-trust-point-input"
              />
              <Button variant="outline" onClick={addTrustPoint}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-2">
              {settings.trust_points?.map((point, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <span className="text-sm text-slate-700">{point}</span>
                  <button
                    onClick={() => removeTrustPoint(index)}
                    className="text-slate-400 hover:text-red-600"
                    data-testid={`remove-trust-point-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Display Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Отображение блоков</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>FAQ секция</Label>
                <p className="text-sm text-slate-500">Показывать блок FAQ</p>
              </div>
              <Switch
                checked={settings.show_faq}
                onCheckedChange={(checked) => setSettings({ ...settings, show_faq: checked })}
                data-testid="settings-show-faq-switch"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Галерея фото</Label>
                <p className="text-sm text-slate-500">Показывать галерею</p>
              </div>
              <Switch
                checked={settings.show_gallery}
                onCheckedChange={(checked) => setSettings({ ...settings, show_gallery: checked })}
                data-testid="settings-show-gallery-switch"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Видео</Label>
                <p className="text-sm text-slate-500">Показывать видео</p>
              </div>
              <Switch
                checked={settings.show_videos}
                onCheckedChange={(checked) => setSettings({ ...settings, show_videos: checked })}
                data-testid="settings-show-videos-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Global contact button toggles */}
        <Card>
          <CardHeader>
            <CardTitle>Кнопки связи (для всех менеджеров)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 -mt-2">
              Глобальный тумблер поверх настроек каждого менеджера. Если здесь выключено —
              кнопка не показывается ни у кого, независимо от того, что стоит в карточке менеджера.
            </p>
            <div className="flex items-center justify-between">
              <div>
                <Label>Telegram</Label>
                <p className="text-sm text-slate-500">Показывать Telegram-кнопки на сайте</p>
              </div>
              <Switch
                checked={settings.enable_telegram !== false}
                onCheckedChange={(checked) => setSettings({ ...settings, enable_telegram: checked })}
                data-testid="settings-enable-telegram-switch"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>WhatsApp</Label>
                <p className="text-sm text-slate-500">Показывать WhatsApp-кнопки на сайте</p>
              </div>
              <Switch
                checked={settings.enable_whatsapp !== false}
                onCheckedChange={(checked) => setSettings({ ...settings, enable_whatsapp: checked })}
                data-testid="settings-enable-whatsapp-switch"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
